import { useState } from "react";
import { buttonClass } from "@/design/buttons";

/**
 * The API could not be reached (R22 T10).
 *
 * **The unhappy path the split creates.** On the Next host an unreachable
 * backend meant a page that never rendered, and the browser showed its own
 * error; here the app is already running in the browser when the API goes away,
 * so it has to say so itself. It is worth being careful about: the two lazy
 * answers are to treat it as "signed out", which throws away a session over a
 * dropped packet, and to keep waiting, which shows a skeleton until somebody
 * gives up.
 *
 * Retry is manual rather than automatic. An app that retries by itself hides
 * the outage while making it worse, and the person reading this knows whether
 * their train just entered a tunnel.
 */
export function ScreenUnreachable({ onRetry }: { onRetry: () => void }) {
  const [retrying, setRetrying] = useState(false);

  return (
    <div className="mx-auto flex w-full max-w-2xl grow flex-col items-start px-4 py-20 sm:px-6">
      <h1 className="font-display text-2xl font-bold text-ink">
        We cannot reach Bookr right now
      </h1>
      <p className="mt-3 text-body">
        This is usually a connection problem. Check that you are online, then
        try again.
      </p>
      <button
        type="button"
        className={`${buttonClass("primary")} mt-8`}
        disabled={retrying}
        onClick={() => {
          setRetrying(true);
          onRetry();
          // The provider swaps this screen out the moment it has an answer, so
          // the only job here is to stop a second click landing meanwhile.
          window.setTimeout(() => setRetrying(false), 2000);
        }}
      >
        {retrying ? "Trying again" : "Try again"}
      </button>
    </div>
  );
}
